import { useMemo, useState } from 'react';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  PaginationState,
  flexRender,
  getPaginationRowModel,
  getFilteredRowModel,
  SortingState,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useDebounce } from '@/hooks/use-debounce';
import { TablePagination } from './table-pagination';
import { TableSearch } from './table-search';
import { TableLoading } from './table-loading';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

interface TableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  isLoading?: boolean;
  manualPagination?: boolean;
  pageCount?: number;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onGlobalFilterChange?: (value: string) => void;
  showSearch?: boolean;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
}

export function Table<TData>({
  columns,
  data,
  isLoading = false,
  manualPagination = false,
  pageCount,
  onPaginationChange,
  onSortingChange,
  onGlobalFilterChange,
  showSearch = true,
  onRowSelectionChange,
  rowSelection = {},
}: TableProps<TData>) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchValue, setSearchValue] = useState('');
  const globalFilter = useDebounce(searchValue, 100);
  const defaultData = useMemo(() => [], []);
  const [sorting, setSorting] = useState<SortingState>([]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue
  ) => {
    const newPagination =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(pagination)
        : updaterOrValue;

    setPagination(newPagination);
    if (manualPagination && onPaginationChange) {
      onPaginationChange(newPagination);
    }
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting)
        : updaterOrValue;

    setSorting(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
  };

  const handleGlobalFilterChange = (value: string) => {
    setSearchValue(value);
    if (onGlobalFilterChange) {
      onGlobalFilterChange(value);
    }
  };

  const table = useReactTable({
    data: data || defaultData,
    columns,
    state: {
      pagination,
      sorting,
      globalFilter: showSearch ? globalFilter : undefined,
      rowSelection,
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: pageCount ?? -1,
        }
      : {
          getPaginationRowModel: getPaginationRowModel(),
        }),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: !!rowSelection,
    enableMultiRowSelection: false,
    onRowSelectionChange,
  });

  return (
    <div className="space-y-4">
      {showSearch && (
        <TableSearch
          searchValue={searchValue}
          setSearchValue={handleGlobalFilterChange}
        />
      )}

      <div className="relative overflow-x-auto">
        {isLoading ? <TableLoading /> : null}

        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const thClassName = clsx(
                    'text-sm font-semibold text-gray-900 dark:text-gray-100',
                    header.column.getIsFirstColumn() &&
                      'py-3.5 pl-4 pr-3 sm:pl-6 text-left',
                    !header.column.getIsFirstColumn() &&
                      !header.column.getIsLastColumn() &&
                      'px-3 py-3.5 text-left',
                    header.column.getIsLastColumn() &&
                      'pr-4 sm:pr-6 text-right',
                    header.column.getCanSort() && 'cursor-pointer select-none'
                  );

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={thClassName}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className="flex items-center gap-1"
                          onClick={header.column.getToggleSortingHandler()}
                          title={
                            header.column.getCanSort()
                              ? header.column.getNextSortingOrder() === 'asc'
                                ? 'Sort ascending'
                                : header.column.getNextSortingOrder() === 'desc'
                                ? 'Sort descending'
                                : 'Clear sort'
                              : undefined
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <ChevronUpIcon className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                            ),
                            desc: (
                              <ChevronDownIcon className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                            ),
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={
                  onRowSelectionChange
                    ? row.getToggleSelectedHandler()
                    : undefined
                }
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                {row.getVisibleCells().map((cell) => {
                  const isSelected = row.getIsSelected();

                  const tdClassName = clsx(
                    'whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 py-3',
                    cell.column.getIsFirstColumn() && 'pl-4 pr-3 sm:pl-6',
                    !cell.column.getIsFirstColumn() &&
                      !cell.column.getIsLastColumn() &&
                      'px-3',
                    cell.column.getIsLastColumn() && 'pr-4 sm:pr-6',
                    isSelected && 'bg-gray-100'
                  );
                  return (
                    <td key={cell.id} className={tdClassName}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TablePagination table={table} />
    </div>
  );
}
